"use client";
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, message, Spin } from "antd";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";

const EditUserModal = ({ open, onCancel, onSuccess, userId }) => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(false);

  // Fetch roles
  const getRoles = async () => {
    setRolesLoading(true);
    try {
      const res = await post(ApiURL.list_roles, new FormData());
      if (res?.status === true) {
        setRoles(res.data);
      } else {
        message.error(res?.message || "Failed to fetch roles");
      }
    } catch (err) {
      message.error("Error fetching roles");
    } finally {
      setRolesLoading(false);
    }
  };

  // Fetch user details
  const getUserDetails = async () => {
    if (!userId) return;
    setFetchingUser(true);
    try {
      const formData = new FormData();
      formData.append("user_id", userId);
      const res = await post(ApiURL.get_user_details, formData);
      if (res?.status === true) {
        const userData = res.data;
        form.setFieldsValue({
          user_id: userData.id,
          name: userData.name,
          email: userData.email,
          phone_number: userData.phone_number,
          admin_role: userData.role || userData.admin_role,
        });
      } else {
        message.error(res?.message || "Failed to fetch user details");
      }
    } catch (err) {
      message.error("Error fetching user details");
    } finally {
      setFetchingUser(false);
    }
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (open && userId) {
      getRoles();
      getUserDetails();
    } else {
      form.resetFields();
    }
  }, [open, userId, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("user_id", userId);
      Object.entries(values).forEach(([key, val]) => {
        if (key === "user_id") return;
        formData.append(key, val);
      });

      const res = await post(ApiURL.user_edit, formData);

      if (res?.status === true) {
        message.success(res?.message || "User updated successfully");
        onSuccess();
        onCancel();
        return;
      }

      const errorMsg = res?.error || res?.response?.data?.error || res?.message || res?.response?.data?.message;
      if (errorMsg) {
        message.error(errorMsg);
      } else {
        message.error("Something went wrong while updating user");
      }
    } catch (error) {
      console.error(error);
      const apiError = error?.response?.data?.error || error?.message;
      message.error(apiError || "Error while submitting form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit User"
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
      destroyOnClose
    >
      <Spin spinning={fetchingUser}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <Form.Item name="user_id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input placeholder="Enter name" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Invalid email format" },
            ]}
          >
            <Input placeholder="Enter email" disabled autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="phone_number"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter phone number" },
              { pattern: /^[0-9]{10}$/, message: "Enter valid 10-digit number" },
            ]}
          >
            <Input 
              placeholder="Enter phone number" 
              autoComplete="off"
              maxLength={10}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); 
                form.setFieldsValue({ phone_number: value });
              }}
            />
          </Form.Item>

          <Form.Item
            name="admin_role"
            label="Role"
            rules={[{ required: true, message: "Please select a role" }]}
          >
            <Select
              placeholder="Select Role"
              loading={rolesLoading}
              notFoundContent={rolesLoading ? "Loading..." : "No roles found"}
            >
              {roles.map((r) => (
                <Select.Option key={r.key} value={r.key}>
                  {r.value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ width: "100%", marginTop: 10, backgroundColor: "#E11236", border: "none" }}
          >
            Update User
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditUserModal;
